import React, { useState } from 'react';
import LoadingScreen from '../Layout/LoadingScreen';
import { VinCentrixLogo } from '../Logo/VinCentrixLogo';

const LoginPage = ({ onLogin }) => {
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState('');
  const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isLoggingIn) return;

    const email = e.target.email.value.trim();
    const password = e.target.password.value;

    setIsLoggingIn(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      if (!response.ok || !data.success || !data.token || !data.dealership?.id) {
        throw new Error(data.message || 'Invalid username or password');
      }

      const dealerData = {
        id: data.dealership.id,
        name: data.dealership.name,
        email: data.user?.email || email,
        firstName: data.user?.firstName || '',
        lastName: data.user?.lastName || '',
        role: data.user?.role || '',
        title: data.user?.title || ''
      };

      // Persist the full authenticated session before leaving the login page.
      // The dashboard then boots from storage rather than depending on an in-memory
      // React state transition that can race or fail during a production bundle swap.
      localStorage.setItem('dealer', JSON.stringify(dealerData));
      localStorage.setItem('dealership', JSON.stringify(dealerData));
      localStorage.setItem('vincentrix_dealer', JSON.stringify(dealerData));
      localStorage.setItem('token', data.token);
      localStorage.setItem('vincentrix_token', data.token);
      localStorage.setItem('vincentrix_user', JSON.stringify(dealerData));

      // Keep the parent state in sync when possible, but never let an error in that
      // compatibility callback block a valid login from reaching the dashboard.
      try {
        if (onLogin) onLogin(dealerData, data.token);
      } catch (handoffError) {
        console.error('VinCentrix in-memory login handoff failed; continuing with persisted session:', handoffError);
      }

      // Force a clean application boot. App.js and useCustomers will initialize from
      // the persisted dealer/token and immediately begin authenticated dashboard loads.
      window.location.replace('/dashboard');
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message || 'Unable to sign in');
      setIsLoggingIn(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#000000',
      backgroundImage: `
        radial-gradient(ellipse 80% 50% at 50% -20%, rgba(59, 130, 246, 0.15), transparent),
        radial-gradient(ellipse 60% 40% at 100% 100%, rgba(59, 130, 246, 0.1), transparent),
        radial-gradient(ellipse 40% 60% at 0% 100%, rgba(99, 102, 241, 0.08), transparent)
      `,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      position: 'relative'
    }}>
      {isLoggingIn && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          background: '#000000'
        }}>
          <LoadingScreen />
        </div>
      )}

      <div style={{
        position: 'relative',
        background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.02) 100%)',
        backdropFilter: 'blur(40px) saturate(180%)',
        WebkitBackdropFilter: 'blur(40px) saturate(180%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '24px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
        padding: '48px',
        width: '100%',
        maxWidth: '450px',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)'
        }} />

        <div style={{ textAlign: 'center', marginBottom: '40px', position: 'relative', zIndex: 1 }}>
          <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'center' }}>
            <VinCentrixLogo size={80} showText={false} />
          </div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '600',
            color: '#ffffff',
            marginBottom: '12px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
            textShadow: '0 0 40px rgba(255, 255, 255, 0.1)'
          }}>Welcome Back</h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '15px',
            letterSpacing: '0.5px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
          }}>Sign in to continue</p>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#ef4444',
            padding: '12px 16px',
            borderRadius: '12px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>{error}</div>
        )}

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: 'rgba(255, 255, 255, 0.4)',
              fontSize: '11px',
              fontWeight: '600',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              marginBottom: '12px'
            }}>Email</label>
            <input
              type="email"
              name="email"
              required
              placeholder="Enter your email"
              disabled={isLoggingIn}
              autoComplete="username"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '16px 20px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '14px',
                color: '#fff',
                fontSize: '15px',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              color: 'rgba(255, 255, 255, 0.4)',
              fontSize: '11px',
              fontWeight: '600',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              marginBottom: '12px'
            }}>Password</label>
            <input
              type="password"
              name="password"
              required
              placeholder="Enter your password"
              disabled={isLoggingIn}
              autoComplete="current-password"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '16px 20px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '14px',
                color: '#fff',
                fontSize: '15px',
                outline: 'none'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={isLoggingIn}
            style={{
              width: '100%',
              padding: '16px 28px',
              background: isLoggingIn
                ? 'rgba(59, 130, 246, 0.5)'
                : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
              border: 'none',
              borderRadius: '14px',
              color: '#fff',
              fontSize: '15px',
              fontWeight: '600',
              letterSpacing: '0.5px',
              cursor: isLoggingIn ? 'not-allowed' : 'pointer',
              boxShadow: isLoggingIn ? 'none' : '0 0 20px rgba(59, 130, 246, 0.5), 0 4px 15px rgba(59, 130, 246, 0.3)'
            }}
          >{isLoggingIn ? 'Signing In…' : 'Sign In'}</button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
